import { supabase } from '@/integrations/supabase/client';

export interface UserSenderIdentity {
  id: string;
  user_id: string;
  company_id: string;
  sendgrid_sender_id: string | null;
  nickname: string;
  from_email: string;
  from_name: string;
  reply_to_email: string | null;
  verification_status: 'pending' | 'verified' | 'failed' | 'not_started';
  verification_sent_at: string | null;
  verified_at: string | null;
  verification_error: string | null;
  last_verification_check: string | null;
  is_active: boolean;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Get all sender identities for the current user
 */
export const getUserSenderIdentities = async (
  userId: string
): Promise<UserSenderIdentity[]> => {
  try {
    const { data, error } = await supabase
      .from('user_sender_identities')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
    
    if (error) {
      console.error('Error fetching sender identities:', error);
      throw error;
    }
    
    return (data || []) as UserSenderIdentity[];
  } catch (error) {
    console.error('Error in getUserSenderIdentities:', error);
    throw error;
  }
};

/**
 * Get the default verified sender identity for the current user
 */
export const getDefaultSenderIdentity = async (
  userId: string
): Promise<UserSenderIdentity | null> => {
  try {
    const { data, error } = await supabase
      .from('user_sender_identities')
      .select('*')
      .eq('user_id', userId)
      .eq('is_default', true)
      .eq('verification_status', 'verified')
      .single();
    
    if (error) {
      if (error.code === 'PGRST116') {
        // No default sender found
        return null;
      }
      console.error('Error fetching default sender identity:', error);
      throw error;
    }
    
    return data as UserSenderIdentity;
  } catch (error) {
    console.error('Error in getDefaultSenderIdentity:', error);
    return null;
  }
};

/**
 * Create a new sender identity and trigger verification
 */
export const createSenderIdentity = async (payload: {
  nickname: string;
  from_email: string;
  from_name: string;
  reply_to_email?: string;
}): Promise<{ success: boolean; sender_id?: string; error?: string }> => {
  try {
    const { data, error } = await supabase.functions.invoke('verify-sendgrid-sender', {
      body: payload
    });
    
    if (error) {
      console.error('Error creating sender identity:', error);
      throw error;
    }
    
    return data;
  } catch (error: any) {
    console.error('Error in createSenderIdentity:', error);
    throw error;
  }
};

/**
 * Check the verification status of a sender identity
 */
export const checkSenderVerification = async (
  senderIdentityId: string
): Promise<{ success: boolean; status: string; verified: boolean; message?: string }> => {
  try {
    const { data, error } = await supabase.functions.invoke('check-sendgrid-verification', {
      body: { sender_identity_id: senderIdentityId }
    });
    
    if (error) {
      console.error('Error checking verification:', error);
      throw error;
    }
    
    return data;
  } catch (error: any) {
    console.error('Error in checkSenderVerification:', error);
    throw error;
  }
};

/**
 * Resend the verification email for a sender identity
 */
export const resendSenderVerification = async (
  senderIdentityId: string
): Promise<{ success: boolean; message?: string }> => {
  try {
    const { data, error } = await supabase.functions.invoke('resend-sendgrid-verification', {
      body: { sender_identity_id: senderIdentityId }
    });
    
    if (error) {
      console.error('Error resending verification:', error);
      throw error;
    }
    
    return data;
  } catch (error: any) {
    console.error('Error in resendSenderVerification:', error);
    throw error;
  }
};

/**
 * Start verification for an existing (migrated) sender identity
 */
export const initiateSenderVerification = async (sender: UserSenderIdentity): Promise<{ success: boolean; sender_id?: string; error?: string }> => {
  try {
    const { data, error } = await supabase.functions.invoke('verify-sendgrid-sender', {
      body: {
        nickname: sender.nickname,
        from_email: sender.from_email,
        from_name: sender.from_name,
        reply_to_email: sender.reply_to_email || undefined,
        existing_sender_id: sender.id,
      }
    });
    if (error) {
      console.error('Error initiating sender verification:', error);
      throw error;
    }
    return data;
  } catch (error: any) {
    console.error('Error in initiateSenderVerification:', error);
    throw error;
  }
};

/**
 * Set a sender identity as the default
 */
export const setDefaultSender = async (
  senderIdentityId: string,
  userId: string
): Promise<void> => {
  try {
    const { error } = await supabase
      .from('user_sender_identities')
      .update({ is_default: true })
      .eq('id', senderIdentityId)
      .eq('user_id', userId);
    
    if (error) {
      console.error('Error setting default sender:', error);
      throw error;
    }
  } catch (error) {
    console.error('Error in setDefaultSender:', error);
    throw error;
  }
};

/**
 * Delete a sender identity (from both SendGrid and database)
 */
export const deleteSenderIdentity = async (
  senderIdentityId: string
): Promise<{ success: boolean; message?: string }> => {
  try {
    const { data, error } = await supabase.functions.invoke('delete-sendgrid-sender', {
      body: { sender_identity_id: senderIdentityId }
    });
    
    if (error) {
      console.error('Error deleting sender identity:', error);
      throw error;
    }
    
    return data;
  } catch (error: any) {
    console.error('Error in deleteSenderIdentity:', error);
    throw error;
  }
};
